import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Avatar,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import {
    Search as SearchIcon,
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import roleAdminService from '../../../services/roleAdminService';
import { useSnackbar } from 'notistack';
import UserRoleAssignDialog from './UserRoleAssignDialog';

const RoleUsers = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [role, setRole] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [userToRevoke, setUserToRevoke] = useState(null);

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Récupérer le rôle
            const roleData = await roleAdminService.getRole(id);
            setRole(roleData);

            // Récupérer les utilisateurs du rôle
            const roleUsers = await roleAdminService.getRoleUsers(id);
            setUsers(roleUsers);
        } catch (error) {
            enqueueSnackbar('Erreur lors du chargement des données', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/admin/roles');
    };

    const handleOpenAssignDialog = () => {
        setAssignDialogOpen(true);
    };

    const handleCloseAssignDialog = () => {
        setAssignDialogOpen(false);
    };

    const handleAssignRole = async (userId) => {
        try {
            await roleAdminService.assignRoleToUser(userId, id);
            enqueueSnackbar('Rôle attribué avec succès', { variant: 'success' });
            fetchData();
        } catch (error) {
            enqueueSnackbar('Erreur lors de l\'attribution du rôle', { variant: 'error' });
        } finally {
            setAssignDialogOpen(false);
        }
    };

    const handleRevokeClick = (user) => {
        setUserToRevoke(user);
        setRevokeDialogOpen(true);
    };

    const handleRevokeConfirm = async () => {
        if (!userToRevoke) return;

        try {
            await roleAdminService.revokeRoleFromUser(userToRevoke.id, id);
            enqueueSnackbar('Rôle révoqué avec succès', { variant: 'success' });
            fetchData();
        } catch (error) {
            enqueueSnackbar('Erreur lors de la révocation du rôle', { variant: 'error' });
        } finally {
            setRevokeDialogOpen(false);
            setUserToRevoke(null);
        }
    };

    const handleRevokeCancel = () => {
        setRevokeDialogOpen(false);
        setUserToRevoke(null);
    };

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading && !role) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (!role) {
        return (
            <Box>
                <Typography variant="h5" color="error">
                    Rôle non trouvé
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mt: 2 }}
                >
                    Retour à la liste des rôles
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Utilisateurs avec le rôle: {role.name}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                >
                    Retour
                </Button>
            </Box>

            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="body1" mb={2}>
                    {role.description}
                </Typography>

                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="bold">Type:</Typography>
                    <Typography variant="body2">{role.isSystem ? 'Système' : 'Personnalisé'}</Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="bold">Statut:</Typography>
                    <Typography variant="body2">{role.isActive ? 'Actif' : 'Inactif'}</Typography>
                </Box>
            </Paper>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <TextField
                    placeholder="Rechercher un utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: '60%' }}
                />

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PersonAddIcon />}
                    onClick={handleOpenAssignDialog}
                    disabled={!role.isActive}
                >
                    Attribuer à un utilisateur
                </Button>
            </Box>

            <Paper elevation={2}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Utilisateur</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Statut</TableCell>
                                <TableCell>Rôles</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Aucun utilisateur trouvé
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredUsers.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Avatar src={user.profilePicture} alt={user.name}>
                                                    {user.name.charAt(0)}
                                                </Avatar>
                                                <Typography>{user.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.verificationStatus === 'verified' ? 'Vérifié' : 'En attente'}
                                                color={user.verificationStatus === 'verified' ? 'success' : 'warning'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                {user.isAdmin && (
                                                    <Chip label="Admin" size="small" color="primary" />
                                                )}
                                                {user.isOwner && (
                                                    <Chip label="Propriétaire" size="small" color="info" />
                                                )}
                                                {user.isTenant && (
                                                    <Chip label="Locataire" size="small" color="success" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRevokeClick(user)}
                                                disabled={!role.isActive}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Dialog d'attribution de rôle */}
            <UserRoleAssignDialog
                open={assignDialogOpen}
                onClose={handleCloseAssignDialog}
                onAssign={handleAssignRole}
                roleId={id}
                roleName={role.name}
            />

            {/* Dialog de confirmation de révocation */}
            <Dialog
                open={revokeDialogOpen}
                onClose={handleRevokeCancel}
            >
                <DialogTitle>Confirmer la révocation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir révoquer le rôle "{role.name}" de l'utilisateur "{userToRevoke?.name}" ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRevokeCancel}>
                        Annuler
                    </Button>
                    <Button onClick={handleRevokeConfirm} color="error">
                        Révoquer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RoleUsers;